/**
 * TypeScript wrappers for the WASM shrinkwrap module.
 *
 * Two consumers:
 *   * `viewport/modifiers/shrinkwrapCore.ts` — one-shot per-modifier
 *     evaluation via {@link evaluateShrinkwrap}.
 *   * `modeling/sceneSurface.ts` — stateful per-surface {@link FaceGrid}
 *     handle, built once and queried repeatedly during edit-mode soft-snap.
 *
 * The WASM `FaceGrid` owns its positions+indices, so callers may free their
 * source typed arrays as soon as the grid is constructed.
 */

import {
  FaceGrid as WasmFaceGrid,
  shrinkwrap_evaluate as wasmShrinkwrapEvaluate,
} from "./pkg/poseref_wasm.js";

export interface SurfaceHitResult {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
  distSq: number;
}

export type ShrinkwrapWrapMode =
  | "nearest_surface"
  | "nearest_vertex"
  | "project";
export type ShrinkwrapProjectAxis = "x" | "y" | "z" | "normal";

const WRAP_MODE_CODES: Record<ShrinkwrapWrapMode, number> = {
  nearest_surface: 0,
  nearest_vertex: 1,
  project: 2,
};

const PROJECT_AXIS_CODES: Record<ShrinkwrapProjectAxis, number> = {
  x: 0,
  y: 1,
  z: 2,
  normal: 3,
};

const EMPTY_F32 = new Float32Array(0);
const EMPTY_U32 = new Uint32Array(0);

function unpackHit(packed: Float32Array): SurfaceHitResult | null {
  if (packed.length < 7) return null;
  return {
    x: packed[0],
    y: packed[1],
    z: packed[2],
    nx: packed[3],
    ny: packed[4],
    nz: packed[5],
    distSq: packed[6],
  };
}

/**
 * Persistent face grid for closest-point-on-surface queries. Mirrors the
 * `SpatialGrid` pattern in `spatialIndex.ts`: holds an opaque WASM handle and
 * exposes `dispose()` to release it.
 */
export class FaceGrid {
  private readonly handle: WasmFaceGrid;

  constructor(positions: Float32Array, indices: Uint32Array) {
    this.handle = new WasmFaceGrid(positions, indices);
  }

  queryNearestSurface(
    px: number,
    py: number,
    pz: number,
  ): SurfaceHitResult | null {
    return unpackHit(this.handle.query_nearest_surface(px, py, pz));
  }

  dispose(): void {
    this.handle.free();
  }
}

export interface ShrinkwrapEvaluateInput {
  srcPositions: Float32Array;
  srcIndices: Uint32Array;
  /** Pass `null` to fall back to area-weighted normals (only used for project + axis=normal). */
  srcNormals: Float32Array | null;
  tgtPositions: Float32Array;
  tgtIndices: Uint32Array;
  /** Optional; only used for nearest_vertex hit-normal output. */
  tgtNormals: Float32Array | null;
  wrapMode: ShrinkwrapWrapMode;
  projectAxis: ShrinkwrapProjectAxis;
  projectPositive: boolean;
  projectNegative: boolean;
  offset: number;
  /** Pre-sorted vertex indices to constrain the operation to. `null` = all vertices. */
  vertexGroupIndices: Uint32Array | null;
}

/**
 * One-shot shrinkwrap evaluation. Returns a fresh positions buffer of the
 * same length as `srcPositions`. Vertices outside the optional vertex group
 * (or vertices with no surface hit) are passed through unchanged.
 */
export function evaluateShrinkwrap(
  input: ShrinkwrapEvaluateInput,
): Float32Array {
  return wasmShrinkwrapEvaluate(
    input.srcPositions,
    input.srcIndices,
    input.srcNormals ?? EMPTY_F32,
    input.tgtPositions,
    input.tgtIndices,
    input.tgtNormals ?? EMPTY_F32,
    WRAP_MODE_CODES[input.wrapMode],
    PROJECT_AXIS_CODES[input.projectAxis],
    input.projectPositive,
    input.projectNegative,
    input.offset,
    input.vertexGroupIndices ?? EMPTY_U32,
  );
}
